import { MESSAGES } from "@/constants";
import { formatDateTimeWithTz } from "@/utils/date";

/**
 * The Slack OAuth callback (iq-backend `slack_oauth_callback`) lands the admin on
 * `/settings/integrations?slack=connected|error[&reason=…]` (`?pulse=` for the
 * IQ Data Engineer app). An admin who is not signed in to IQ is bounced through
 * `/auth` and accounts login, which drop the query and land on the default
 * route. We stash the params in localStorage at boot (before any redirect), so
 * the stash survives a login that finishes in another tab; `Auth` sends a fresh
 * stash back to Integrations, which reads and clears it.
 */

const SLACK_OAUTH_REASONS = ["expired", "session_expired", "invalid", "cancelled", "failed"] as const;

/** Why the backend rejected the OAuth return. Anything else is treated as generic. */
export type SlackOAuthReason = (typeof SLACK_OAUTH_REASONS)[number];

export interface SlackOAuthRedirect {
  iq: string | null;
  pulse: string | null;
  reason: SlackOAuthReason | null;
}

export const SLACK_OAUTH_REDIRECT_STORAGE_KEY = "slack_oauth_redirect";

/** A stash older than this is from an abandoned visit and must not raise a banner. */
export const SLACK_OAUTH_REDIRECT_TTL_MS = 30 * 60 * 1000;

interface StoredRedirect extends SlackOAuthRedirect {
  savedAt: number;
}

function toReason(value: string | null): SlackOAuthReason | null {
  return SLACK_OAUTH_REASONS.find((reason) => reason === value) ?? null;
}

function parseSearch(search: string): SlackOAuthRedirect | null {
  const params = new URLSearchParams(search);
  const iq = params.get("slack");
  const pulse = params.get("pulse");
  if (!iq && !pulse) return null;
  return { iq, pulse, reason: toReason(params.get("reason")) };
}

// Storage is resolved inside each caller's try: reading `window.localStorage`
// itself throws when site data is blocked, and the stash runs at boot.

/** Saves the OAuth-return params from `search`, if any. Call once at boot. */
export function stashSlackOAuthRedirect(
  search: string = window.location.search,
  storage?: Storage,
  now: number = Date.now(),
): void {
  const redirect = parseSearch(search);
  if (!redirect) return;
  try {
    const stored: StoredRedirect = { ...redirect, savedAt: now };
    (storage ?? window.localStorage).setItem(SLACK_OAUTH_REDIRECT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage blocked (private mode / quota): the URL params still work when signed in.
  }
}

/**
 * The OAuth-return params for this visit: the URL wins, else a fresh stash left
 * by {@link stashSlackOAuthRedirect} before the login round-trip. Pure — safe in
 * a `useState` initializer; clear the stash with {@link clearSlackOAuthRedirect}.
 */
export function readSlackOAuthRedirect(
  search: string = window.location.search,
  storage?: Storage,
  now: number = Date.now(),
): SlackOAuthRedirect {
  const fromUrl = parseSearch(search);
  if (fromUrl) return fromUrl;
  const empty: SlackOAuthRedirect = { iq: null, pulse: null, reason: null };
  try {
    const raw = (storage ?? window.localStorage).getItem(SLACK_OAUTH_REDIRECT_STORAGE_KEY);
    if (!raw) return empty;
    const stored = JSON.parse(raw) as Partial<StoredRedirect>;
    if (typeof stored.savedAt !== "number" || now - stored.savedAt > SLACK_OAUTH_REDIRECT_TTL_MS) return empty;
    return {
      iq: typeof stored.iq === "string" ? stored.iq : null,
      pulse: typeof stored.pulse === "string" ? stored.pulse : null,
      reason: toReason(stored.reason ?? null),
    };
  } catch {
    return empty;
  }
}

/** Drops the stash so the banner shows for one visit only. */
export function clearSlackOAuthRedirect(storage?: Storage): void {
  try {
    (storage ?? window.localStorage).removeItem(SLACK_OAUTH_REDIRECT_STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

/** "This link works until Oct 18, 2026, 21:16 GMT+5:30", or null if `expiresAt` is unusable. */
export function shareLinkExpiryLine(expiresAt: string | null | undefined): string | null {
  const when = formatDateTimeWithTz(expiresAt);
  return when ? MESSAGES.SLACK.CONNECT_DIALOG.LINK_EXPIRES_AT(when) : null;
}
